import { EntityGraph } from '@/modules/entityMap/types';
import { AfterDeath, TileNotesInput } from '@/types/schema';

import { isSupportedTileNotesEntityKind } from './types';

/**
 * Builds TileNotesInput entries for tiles on the household entity map
 * (ownership / beneficiary graph). Mirrors buildEntityDiagramTileNotesInput,
 * with two differences specific to the entity map:
 *
 * - The GraphQL entity fragment sits directly on the node's `data` (not
 *   `data.node` as on the per-entity diagram), and the entity kind is exposed
 *   as `data.kind`. Only `data.__typename === 'Entity'` tiles carry an entity;
 *   individuals, organizations, and the grantor business-entities summary tile
 *   are other typenames and are skipped.
 * - The entity map describes the current ownership state, so notes never carry
 *   a death section — always AfterDeath.None.
 *
 * Tiles that can't generate notes (unsupported entity kinds like personal
 * accounts, non-entity tiles, text notes not in the graph) are silently
 * skipped, so callers can treat an empty result as "nothing to generate for".
 */
export function buildEntityMapTileNotesInput(
  tileIds: string[],
  entityGraph: EntityGraph
): TileNotesInput[] {
  const inputs: TileNotesInput[] = [];

  for (const tileId of tileIds) {
    let attrs;
    try {
      attrs = entityGraph.getNodeAttributes(tileId);
    } catch {
      // graphology throws when the node doesn't exist in the graph
      continue;
    }

    const data = attrs?.data;
    if (data?.__typename !== 'Entity') continue;
    if (!isSupportedTileNotesEntityKind(data.kind)) continue;

    inputs.push({
      tileID: tileId,
      deathSection: AfterDeath.None,
      entityIDs: [data.id],
    });
  }

  return inputs;
}
